import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppearance } from '../../../../context/AppearanceContext';

import CohortNode from '../graph/CohortNode';
import { layoutGraph } from '../graph/layout';
import { demoEdges, demoNodes, generateDemoTree } from '../__mocks__/mockGraph';
import {
  buildGraphStateFromReactFlow,
  computeGraphStatistics,
  convertGraphStateToReactFlow,
  createEmptyGraphState,
  deserializeImportedGraph,
  getDescendantIds,
  reduceGraphAction,
  serializeGraphForExport,
} from '../graph/state/graphModel';
import { GRAPH_ACTIONS } from '../graph/state/graphActions';
import { useGraphImportExport } from './useGraphImportExport';
import { useGraphHistory } from './useGraphHistory';
import { useGraphPersistence } from './useGraphPersistence';
import { useCriteriaEditor } from './useCriteriaEditor';
import { useGraphExecution } from './useGraphExecution';
import { useGraphViewport } from './useGraphViewport';
import { useGraphNodeOperations } from './useGraphNodeOperations';
import { useParameterListPicker } from './useParameterListPicker';

export const useCohortBuilderController = ({ graphState, dispatchGraph, onPersistGraph, cohortId }) => {
  const { formatDate } = useAppearance();
  const graphStateRef = useRef(graphState);
  const importFileInputRef = useRef(null);

  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [feedback, setFeedback] = useState({
    open: false,
    severity: 'info',
    message: '',
  });

  useEffect(() => {
    graphStateRef.current = graphState;
  }, [graphState]);

  const showFeedback = useCallback((message, severity = 'success') => {
    setFeedback({ open: true, severity, message });
  }, []);

  const handleCloseFeedback = useCallback((_, reason) => {
    if (reason === 'clickaway') return;
    setFeedback((current) => ({ ...current, open: false }));
  }, []);

  const {
    showWelcomeTour,
    dismissWelcomeTour,
    persistDraftNow,
  } = useGraphPersistence({
    graphState,
    graphStateRef,
    serializeGraphForExport,
    showFeedback,
    persistRemoteNow: onPersistGraph,
  });

  const {
    historyPast,
    historyFuture,
    applyGraphAction,
    undo,
    redo,
  } = useGraphHistory({
    graphStateRef,
    dispatchGraph,
    reduceGraphAction,
    showFeedback,
    graphActions: GRAPH_ACTIONS,
  });

  const {
    editingNode,
    editNodeId,
    editLabel,
    editDescription,
    editStatus,
    editIncludeCriteria,
    editExcludeCriteria,
    editParameterListName,
    criteriaOperators,
    setEditLabel,
    setEditDescription,
    setEditStatus,
    openEdit,
    closeEdit,
    renameNode,
    addCriterion,
    updateCriterion,
    deleteCriterion,
    insertCriteriaFromParameterList,
    saveEdit,
  } = useCriteriaEditor({
    graphState,
    graphStateRef,
    applyGraphAction,
    showFeedback,
    graphActions: GRAPH_ACTIONS,
  });

  const {
    parameterLists,
    parameterListsLoading,
    parameterListsError,
    loadParameterLists,
    resolveParameterListMembers,
    listMembersById,
    listMembersErrorById,
    expandingListId,
    loadParameterListMembers,
  } = useParameterListPicker();

  const handleInsertParameterList = useCallback(
    async (group, list) => {
      const parameters = await resolveParameterListMembers(list);
      if (parameters.length === 0) {
        showFeedback(`Parameter list "${list?.name || ''}" has no resolvable parameters.`, 'warning');
        return;
      }

      const insertedCount = insertCriteriaFromParameterList(group, list, parameters);
      if (insertedCount === 0) {
        showFeedback(`Parameter list "${list?.name || ''}" has no resolvable parameters.`, 'warning');
        return;
      }

      const nonExecutableCount = parameters.filter((parameter) => parameter?.omopStatus !== 'validated').length;
      if (nonExecutableCount > 0) {
        showFeedback(
          `Inserted ${insertedCount} criteria from "${list.name}" -- ${nonExecutableCount} without an OMOP mapping, marked not executable.`,
          'warning',
        );
        return;
      }

      showFeedback(`Inserted ${insertedCount} criteria from "${list.name}".`, 'success');
    },
    [insertCriteriaFromParameterList, resolveParameterListMembers, showFeedback],
  );

  /**
   * Einsatz eines **einzelnen** Listenelements (AK1, #791) -- derselbe Pfad
   * wie die ganze Liste (`insertCriteriaFromParameterList`), nur mit einem
   * Ein-Element-Array. Das ist AK2 per Konstruktion: es gibt keine zweite
   * Uebersetzung, die vergessen werden koennte.
   */
  const handleInsertParameterListItem = useCallback(
    (group, list, parameter) => {
      const insertedCount = insertCriteriaFromParameterList(group, list, [parameter]);
      if (insertedCount === 0) {
        showFeedback(`Parameter "${parameter?.name || ''}" could not be inserted.`, 'warning');
        return;
      }

      if (parameter?.omopStatus !== 'validated') {
        showFeedback(
          `Inserted "${parameter.name}" from "${list?.name || ''}" -- no OMOP mapping, marked not executable.`,
          'warning',
        );
        return;
      }

      showFeedback(`Inserted "${parameter.name}" from "${list?.name || ''}".`, 'success');
    },
    [insertCriteriaFromParameterList, showFeedback],
  );

  const {
    layoutDirection,
    layoutVersion,
    viewMode,
    expandedNodeIds,
    selectedNodeId,
    setLayoutDirection,
    setViewMode,
    setExpandedNodeIds,
    setSelectedNodeId,
    requestAutoFit,
    toggleExpandedNode,
    focusNodeInGraph,
    handleGraphInit,
    applyAutoLayout,
  } = useGraphViewport({ graphState });

  const {
    deleteDialog,
    requestDeleteNode,
    closeDeleteDialog,
    confirmDeleteNode,
    createRootNode,
    addChild,
    insertBetween,
    toggleBreakpoint,
    duplicateNode,
    loadDemo,
    generateLargeDemo,
    clearGraph,
  } = useGraphNodeOperations({
    graphStateRef,
    applyGraphAction,
    showFeedback,
    requestAutoFit,
    graphActions: GRAPH_ACTIONS,
    getDescendantIds,
    createEmptyGraphState,
    buildGraphStateFromReactFlow,
    demoNodes,
    demoEdges,
    generateDemoTree,
    onCloseEdit: closeEdit,
    editNodeId,
    selectedNodeId,
    setSelectedNodeId,
    setExpandedNodeIds,
  });

  const {
    isExecuting,
    executionProgress,
    executionTargetNodeId,
    executionMode,
    executionBackend,
    setExecutionBackend,
    executionLogs,
    showExecutionLog,
    setShowExecutionLog,
    lastExecutionAt,
    executeGraph,
    executeToHere,
    executeToBreakpoint,
  } = useGraphExecution({
    graphState,
    graphStateRef,
    applyGraphAction,
    showFeedback,
    graphActions: GRAPH_ACTIONS,
    cohortId,
    onPrepareExecution: () => {
      closeEdit();
      closeDeleteDialog();
    },
  });

  const renderGraph = useMemo(() => {
    const { nodes: flowNodes, edges: flowEdges } = convertGraphStateToReactFlow(graphState);
    const selectedGraphNodes = flowNodes.map((node) => ({
      ...node,
      selected: node.id === selectedNodeId,
    }));
    return layoutGraph(selectedGraphNodes, flowEdges, { direction: layoutDirection });
  }, [graphState, layoutDirection, selectedNodeId]);

  const nodes = renderGraph.nodes;
  const edges = renderGraph.edges;

  const graphStatistics = useMemo(() => computeGraphStatistics(graphState), [graphState]);
  const breakpointNodeId = graphState.metadata?.breakpointNodeId || null;
  const breakpointNodeLabel = breakpointNodeId
    ? graphState.nodesById?.[breakpointNodeId]?.name || `Node ${breakpointNodeId}`
    : null;

  const lastExecutionLabel = useMemo(() => {
    if (!lastExecutionAt) return 'Never';
    return formatDate(lastExecutionAt, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) || 'Unknown';
  }, [lastExecutionAt, formatDate]);

  const getNodeStatusColor = useCallback((status) => {
    if (status === 'executed' || status === 'completed') return 'success';
    if (status === 'executing') return 'warning';
    if (status === 'edited') return 'info';
    if (status === 'unmapped') return 'warning';
    if (status === 'error') return 'error';
    return 'default';
  }, []);

  const handleAutoLayout = useCallback(() => {
    if (nodes.length === 0) return;
    applyAutoLayout();
    showFeedback('Auto-layout applied.', 'success');
  }, [applyAutoLayout, nodes.length, showFeedback]);

  const { exportJson, exportSvg, exportPng } = useGraphImportExport({
    getGraphState: () => graphStateRef.current,
    getNodes: () => nodes,
    getEdges: () => edges,
    serializeGraph: serializeGraphForExport,
    showFeedback,
  });

  const handleTriggerImport = useCallback(() => {
    importFileInputRef.current?.click();
  }, []);

  const handleImportFile = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      try {
        const text = await file.text();
        const parsed = JSON.parse(text);
        const importedGraph = deserializeImportedGraph(parsed);

        const changed = applyGraphAction(
          {
            type: GRAPH_ACTIONS.SET_GRAPH,
            payload: importedGraph,
          },
          { recordHistory: true },
        );

        if (changed) {
          setSelectedNodeId(importedGraph.rootNodeId || null);
          showFeedback('Graph imported successfully.', 'success');
          requestAutoFit({ padding: 0.38, duration: 560, maxZoom: 0.9 });
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Import failed.';
        showFeedback(`Import failed: ${message}`, 'error');
      }
    },
    [applyGraphAction, requestAutoFit, setSelectedNodeId, showFeedback],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const isEditableElement = (target) => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName?.toLowerCase();
      return tag === 'input' || tag === 'textarea' || target.isContentEditable;
    };

    const handleShortcut = (event) => {
      const key = String(event.key || '').toLowerCase();
      const hasModifier = event.ctrlKey || event.metaKey;
      if (!hasModifier) return;

      if (key === 's') {
        event.preventDefault();
        persistDraftNow();
        return;
      }

      if (key === 'k' && event.shiftKey) {
        event.preventDefault();
        setShortcutsOpen(true);
        return;
      }

      if (isEditableElement(event.target)) return;

      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        if (!isExecuting) undo();
        return;
      }

      if ((key === 'z' && event.shiftKey) || key === 'y') {
        event.preventDefault();
        if (!isExecuting) redo();
        return;
      }

      if (key === 'enter') {
        event.preventDefault();
        if (!isExecuting) executeGraph();
      }
    };

    window.addEventListener('keydown', handleShortcut);
    return () => {
      window.removeEventListener('keydown', handleShortcut);
    };
  }, [executeGraph, isExecuting, persistDraftNow, redo, undo]);

  const nodeTypes = useMemo(
    () => ({
      cohortNode: (props) => (
        <CohortNode
          {...props}
          onEdit={isExecuting ? undefined : openEdit}
          onDelete={isExecuting ? undefined : requestDeleteNode}
          onAddChild={isExecuting ? undefined : addChild}
          onInsert={isExecuting ? undefined : insertBetween}
          onExecuteToHere={isExecuting ? undefined : executeToHere}
          onToggleBreakpoint={isExecuting ? undefined : toggleBreakpoint}
          onRename={isExecuting ? undefined : renameNode}
        />
      ),
    }),
    [
      addChild,
      executeToHere,
      insertBetween,
      isExecuting,
      openEdit,
      renameNode,
      requestDeleteNode,
      toggleBreakpoint,
    ],
  );

  return {
    importFileInputRef,
    shortcutsOpen,
    setShortcutsOpen,
    feedback,
    handleCloseFeedback,
    showFeedback,
    showWelcomeTour,
    dismissWelcomeTour,
    editingNode,
    editLabel,
    editDescription,
    editStatus,
    editIncludeCriteria,
    editExcludeCriteria,
    editParameterListName,
    criteriaOperators,
    setEditLabel,
    setEditDescription,
    setEditStatus,
    closeEdit,
    saveEdit,
    addCriterion,
    updateCriterion,
    deleteCriterion,
    parameterLists,
    parameterListsLoading,
    parameterListsError,
    loadParameterLists,
    handleInsertParameterList,
    listMembersById,
    listMembersErrorById,
    expandingListId,
    loadParameterListMembers,
    handleInsertParameterListItem,
    deleteDialog,
    closeDeleteDialog,
    confirmDeleteNode,
    isExecuting,
    executionProgress,
    executionTargetNodeId,
    executionMode,
    executionBackend,
    setExecutionBackend,
    executionLogs,
    showExecutionLog,
    setShowExecutionLog,
    graphStatistics,
    lastExecutionLabel,
    breakpointNodeId,
    breakpointNodeLabel,
    graphLastError: graphState.lastError,
    historyPastLength: historyPast.length,
    historyFutureLength: historyFuture.length,
    layoutDirection,
    layoutVersion,
    viewMode,
    expandedNodeIds,
    selectedNodeId,
    setLayoutDirection,
    setViewMode,
    setSelectedNodeId,
    setExpandedNodeIds,
    toggleExpandedNode,
    focusNodeInGraph,
    handleGraphInit,
    requestAutoFit,
    nodes,
    edges,
    nodeTypes,
    getNodeStatusColor,
    createRootNode,
    loadDemo,
    generateLargeDemo,
    handleAutoLayout,
    executeGraph,
    executeToBreakpoint,
    handleTriggerImport,
    exportJson,
    exportPng,
    exportSvg,
    clearGraph,
    undo,
    redo,
    openEdit,
    addChild,
    duplicateNode,
    requestDeleteNode,
    handleImportFile,
  };
};

export default useCohortBuilderController;
